import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

export default function ClientDetail({ client, onBack, onNewCampaign, onOpenCampaign, onCreateBriefs }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [fullClient, setFullClient] = useState(client);
  const [customKeywords, setCustomKeywords] = useState([]);
  const [newKw, setNewKw] = useState({ keyword: '', service: '', notes: '' });
  const [addingKw, setAddingKw] = useState(false);
  const [savingKw, setSavingKw] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => { loadCampaigns(); loadFullClient(); loadCustomKeywords(); }, [client.id]);

  const loadFullClient = async () => {
    // Re-fetch full client record to ensure all fields (phone, email, EAT) are present
    const { data } = await supabase
      .from('clients')
      .select('*')
      .eq('id', client.id)
      .single();
    if (data) setFullClient(data);
  };

  const openEdit = () => {
    setEditForm({
      name:                 fullClient.name || '',
      url:                  fullClient.url || '',
      description:          fullClient.description || '',
      phone:                fullClient.phone || '',
      email:                fullClient.email || '',
      years_trading:        fullClient.years_trading || '',
      team_size:            fullClient.team_size || '',
      accreditations:       fullClient.accreditations || '',
      awards:               fullClient.awards || '',
      clients_served:       fullClient.clients_served || '',
      notable_clients:      fullClient.notable_clients || '',
      stats:                fullClient.stats || '',
      unique_selling_points: fullClient.unique_selling_points || '',
      pricing_info:         fullClient.pricing_info || '',
      include_pricing:      fullClient.include_pricing !== false,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    const { error } = await supabase.from('clients').update(editForm).eq('id', fullClient.id);
    if (!error) {
      setFullClient(prev => ({ ...prev, ...editForm }));
      setEditing(false);
    }
    setSavingEdit(false);
  };

  const loadCustomKeywords = async () => {
    const { data } = await supabase
      .from('custom_keywords')
      .select('*')
      .eq('client_id', client.id)
      .order('created_at', { ascending: false });
    if (data) setCustomKeywords(data);
  };

  const addCustomKeyword = async () => {
    if (!newKw.keyword.trim()) return;
    setSavingKw(true);
    const { data, error } = await supabase
      .from('custom_keywords')
      .insert([{
        client_id: client.id,
        keyword: newKw.keyword.trim(),
        service: newKw.service.trim() || null,
        notes: newKw.notes.trim() || null,
      }])
      .select()
      .single();
    if (!error && data) {
      setCustomKeywords(prev => [data, ...prev]);
      setNewKw({ keyword: '', service: '', notes: '' });
      setAddingKw(false);
    }
    setSavingKw(false);
  };

  const deleteCustomKeyword = async (id) => {
    await supabase.from('custom_keywords').delete().eq('id', id);
    setCustomKeywords(prev => prev.filter(k => k.id !== id));
    setSelectedKeywords(prev => prev.filter(k => k.id !== id));
  };

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('keyword_campaigns')
        .select(`*, keywords (id, keyword, service, location, sector, search_volume, rank, priority)`)
        .eq('client_id', client.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setCampaigns(data || []);
      if (data?.length > 0) setExpandedCampaign(data[0].id);
    } catch (e) {
      setError('Could not load campaigns.');
    }
    setLoading(false);
  };

  const deleteCampaign = async (e, campaignId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this campaign and all its keywords?')) return;
    await supabase.from('keyword_campaigns').delete().eq('id', campaignId);
    setSelectedKeywords([]);
    await loadCampaigns();
  };

  const toggleKeyword = (kw) => {
    const exists = selectedKeywords.find(k => k.id === kw.id);
    if (exists) {
      setSelectedKeywords(prev => prev.filter(k => k.id !== kw.id));
    } else {
      setSelectedKeywords(prev => [...prev, kw]);
    }
  };

  const toggleAll = (keywords) => {
    const allSelected = keywords.every(k => selectedKeywords.find(s => s.id === k.id));
    if (allSelected) {
      setSelectedKeywords(prev => prev.filter(k => !keywords.find(kw => kw.id === k.id)));
    } else {
      const toAdd = keywords.filter(k => !selectedKeywords.find(s => s.id === k.id));
      setSelectedKeywords(prev => [...prev, ...toAdd]);
    }
  };

  const isSelected = (kw) => !!selectedKeywords.find(k => k.id === kw.id);

  const totalKeywords = campaigns.reduce((a, c) => a + (c.keywords?.length || 0), 0);
  const highPriority = campaigns.flatMap(c => c.keywords || []).filter(k => k.priority === 'High').length;

  const getPriorityBadge = (p) => {
    if (p === 'High') return <span className="badge badge-high">High</span>;
    if (p === 'Medium') return <span className="badge badge-medium">Medium</span>;
    if (p === 'Low') return <span className="badge badge-low">Low</span>;
    return null;
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Clients</button>
        <div style={{ flex: 1 }}>
          <div className="page-title">{client.name}</div>
          <div className="page-sub">{client.url}</div>
        </div>
        <button className="btn btn-sm" onClick={openEdit}>Edit client</button>
        <button className="btn btn-primary" onClick={() => onNewCampaign(client)}>
          + New keyword research
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Edit client modal */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--color-background-primary, #fff)', borderRadius: 'var(--border-radius-lg, 12px)', padding: '1.5rem', width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Edit client</div>
              <button className="btn btn-sm" onClick={() => setEditing(false)}>Cancel</button>
            </div>

            <div className="form-row form-row-2 form-group">
              <div><label>Business name</label><input type="text" value={editForm.name || ''} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} /></div>
              <div><label>Website URL</label><input type="url" value={editForm.url || ''} onChange={e => setEditForm(p => ({...p, url: e.target.value}))} /></div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea rows={2} value={editForm.description || ''} onChange={e => setEditForm(p => ({...p, description: e.target.value}))} />
            </div>
            <div className="form-row form-row-2 form-group">
              <div><label>Phone number</label><input type="text" placeholder="e.g. 01737 773 768" value={editForm.phone || ''} onChange={e => setEditForm(p => ({...p, phone: e.target.value}))} /></div>
              <div><label>Email address</label><input type="email" placeholder="e.g. info@business.co.uk" value={editForm.email || ''} onChange={e => setEditForm(p => ({...p, email: e.target.value}))} /></div>
            </div>
            <div className="form-row form-row-2 form-group">
              <div><label>Years trading</label><input type="text" placeholder="e.g. 15 years" value={editForm.years_trading || ''} onChange={e => setEditForm(p => ({...p, years_trading: e.target.value}))} /></div>
              <div><label>Team size</label><input type="text" placeholder="e.g. 12 staff" value={editForm.team_size || ''} onChange={e => setEditForm(p => ({...p, team_size: e.target.value}))} /></div>
            </div>
            <div className="form-group"><label>Accreditations</label><input type="text" placeholder="e.g. Which? Trusted Trader, NCCA member" value={editForm.accreditations || ''} onChange={e => setEditForm(p => ({...p, accreditations: e.target.value}))} /></div>
            <div className="form-group"><label>Awards</label><input type="text" placeholder="e.g. Best Local Business 2023" value={editForm.awards || ''} onChange={e => setEditForm(p => ({...p, awards: e.target.value}))} /></div>
            <div className="form-row form-row-2 form-group">
              <div><label>Clients / jobs completed</label><input type="text" placeholder="e.g. Over 2,000 customers" value={editForm.clients_served || ''} onChange={e => setEditForm(p => ({...p, clients_served: e.target.value}))} /></div>
              <div><label>Notable clients</label><input type="text" placeholder="e.g. NHS, Tesco" value={editForm.notable_clients || ''} onChange={e => setEditForm(p => ({...p, notable_clients: e.target.value}))} /></div>
            </div>
            <div className="form-group"><label>Key stats</label><textarea rows={2} placeholder="e.g. 98% satisfaction rate, 500+ reviews" value={editForm.stats || ''} onChange={e => setEditForm(p => ({...p, stats: e.target.value}))} /></div>
            <div className="form-group"><label>Unique selling points</label><textarea rows={2} placeholder="e.g. Same-day service, eco-friendly" value={editForm.unique_selling_points || ''} onChange={e => setEditForm(p => ({...p, unique_selling_points: e.target.value}))} /></div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ marginBottom: 8 }}>Pricing</label>
              <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0, fontWeight: 400, fontSize: 13 }}>
                  <input type="radio" checked={editForm.include_pricing !== false} onChange={() => setEditForm(p => ({...p, include_pricing: true}))} />
                  Include pricing
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0, fontWeight: 400, fontSize: 13 }}>
                  <input type="radio" checked={editForm.include_pricing === false} onChange={() => setEditForm(p => ({...p, include_pricing: false}))} />
                  Do not mention pricing
                </label>
              </div>
              {editForm.include_pricing !== false && (
                <textarea rows={2} placeholder="e.g. Prices from £25, free quotes available" value={editForm.pricing_info || ''} onChange={e => setEditForm(p => ({...p, pricing_info: e.target.value}))} />
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn" onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={savingEdit}>
                {savingEdit ? <><span className="spinner" /> Saving…</> : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="metric-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="metric-card">
          <div className="metric-label">Campaigns</div>
          <div className="metric-value">{campaigns.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Total keywords</div>
          <div className="metric-value">{totalKeywords}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">High priority</div>
          <div className="metric-value" style={{ color: 'var(--green)' }}>{highPriority}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Custom keywords</div>
          <div className="metric-value">{customKeywords.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Selected</div>
          <div className="metric-value" style={{ color: selectedKeywords.length > 0 ? 'var(--blue-mid)' : 'var(--gray-400)' }}>
            {selectedKeywords.length}
          </div>
        </div>
      </div>

      {/* Sticky action bar when keywords selected */}
      {selectedKeywords.length > 0 && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 10,
          background: 'var(--blue-mid)', color: '#fff',
          borderRadius: 'var(--radius-md)', padding: '12px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16, boxShadow: '0 4px 12px rgba(24,95,165,0.3)'
        }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>
            {selectedKeywords.length} keyword{selectedKeywords.length !== 1 ? 's' : ''} selected
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-sm"
              style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff' }}
              onClick={() => setSelectedKeywords([])}
            >
              Clear
            </button>
            <button
              className="btn btn-sm"
              style={{ background: '#fff', border: 'none', color: 'var(--blue-mid)', fontWeight: 600 }}
              onClick={() => onCreateBriefs(selectedKeywords)}
            >
              Create content briefs →
            </button>
          </div>
        </div>
      )}

      {/* Custom keywords section */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: customKeywords.length > 0 || addingKw ? 16 : 0 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Custom keywords</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
              Non-location keywords e.g. informational, service-only, or brand terms
            </div>
          </div>
          <button className="btn btn-sm" onClick={() => setAddingKw(a => !a)}>
            {addingKw ? 'Cancel' : '+ Add keyword'}
          </button>
        </div>

        {/* Add keyword form */}
        {addingKw && (
          <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius-md)', padding: '12px', marginBottom: 12 }}>
            <div className="form-row form-row-2 form-group">
              <div>
                <label>Keyword *</label>
                <input
                  type="text"
                  placeholder="e.g. how often should you clean carpets"
                  value={newKw.keyword}
                  onChange={e => setNewKw(p => ({ ...p, keyword: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addCustomKeyword()}
                  autoFocus
                />
              </div>
              <div>
                <label>Service (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Carpet cleaning"
                  value={newKw.service}
                  onChange={e => setNewKw(p => ({ ...p, service: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label>Notes (optional)</label>
              <input
                type="text"
                placeholder="e.g. Informational blog post, target featured snippet"
                value={newKw.notes}
                onChange={e => setNewKw(p => ({ ...p, notes: e.target.value }))}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={addCustomKeyword} disabled={savingKw || !newKw.keyword.trim()}>
                {savingKw ? <><span className="spinner" /> Saving…</> : 'Add keyword'}
              </button>
              <button className="btn btn-sm" onClick={() => setAddingKw(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Custom keyword list */}
        {customKeywords.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {customKeywords.length} keyword{customKeywords.length !== 1 ? 's' : ''} — tick to select for content briefs
              </div>
              <button className="btn btn-sm" onClick={() => {
                const allSelected = customKeywords.every(k => selectedKeywords.find(s => s.id === k.id));
                if (allSelected) {
                  setSelectedKeywords(prev => prev.filter(k => !customKeywords.find(c => c.id === k.id)));
                } else {
                  const toAdd = customKeywords.filter(k => !selectedKeywords.find(s => s.id === k.id));
                  setSelectedKeywords(prev => [...prev, ...toAdd]);
                }
              }}>
                {customKeywords.every(k => selectedKeywords.find(s => s.id === k.id)) ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <table className="kw-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Keyword</th>
                  <th>Service</th>
                  <th>Notes</th>
                  <th style={{ width: 32 }}></th>
                </tr>
              </thead>
              <tbody>
                {customKeywords.map((kw) => {
                  const selected = !!selectedKeywords.find(s => s.id === kw.id);
                  return (
                    <tr
                      key={kw.id}
                      onClick={() => toggleKeyword(kw)}
                      style={{ cursor: 'pointer', background: selected ? 'var(--blue-light)' : undefined }}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleKeyword(kw)}
                          onClick={e => e.stopPropagation()}
                          style={{ width: 14, height: 14, cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ fontWeight: selected ? 500 : 400 }}>{kw.keyword}</td>
                      <td style={{ color: 'var(--gray-500)' }}>{kw.service || '—'}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 12 }}>{kw.notes || '—'}</td>
                      <td>
                        <button
                          className="btn btn-sm"
                          onClick={e => { e.stopPropagation(); deleteCustomKeyword(kw.id); }}
                          style={{ color: 'var(--gray-400)', borderColor: 'transparent', padding: '2px 6px' }}
                        >✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {customKeywords.length === 0 && !addingKw && (
          <div style={{ fontSize: 13, color: 'var(--gray-400)', textAlign: 'center', padding: '1rem 0' }}>
            No custom keywords yet — add keywords that don't need a location
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-500)' }}>
          <span className="spinner" style={{ width: 20, height: 20 }} /> Loading…
        </div>
      ) : campaigns.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--gray-700)', marginBottom: 8 }}>No keyword research yet</div>
          <button className="btn btn-primary" onClick={() => onNewCampaign(client)}>+ New keyword research</button>
        </div>
      ) : (
        campaigns.map(campaign => {
          const isExpanded = expandedCampaign === campaign.id;
          const keywords = campaign.keywords || [];
          const date = new Date(campaign.created_at);
          const allSelected = keywords.length > 0 && keywords.every(k => isSelected(k));

          return (
            <div className="card" key={campaign.id} style={{ marginBottom: 12 }}>
              {/* Campaign header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setExpandedCampaign(isExpanded ? null : campaign.id)}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>
                    {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                    {(campaign.services || []).slice(0, 3).join(', ')}
                    {(campaign.towns || []).length > 0 && ` · ${campaign.towns.length} locations`}
                    {` · ${keywords.length} keywords`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    className="btn btn-sm"
                    onClick={e => { e.stopPropagation(); onOpenCampaign(campaign); }}
                  >
                    View report
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={e => deleteCampaign(e, campaign.id)}
                    style={{ color: 'var(--gray-400)', borderColor: 'transparent' }}
                  >✕</button>
                  <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Keyword table */}
              {isExpanded && keywords.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Keywords — tick to select for content briefs
                    </div>
                    <button className="btn btn-sm" onClick={() => toggleAll(keywords)}>
                      {allSelected ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <table className="kw-table">
                    <thead>
                      <tr>
                        <th style={{ width: 32 }}></th>
                        <th>Keyword</th>
                        <th>Location</th>
                        <th className="num">Monthly searches</th>
                        <th className="num">Rank</th>
                        <th>Priority</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keywords
                        .sort((a, b) => (b.search_volume || 0) - (a.search_volume || 0))
                        .map((kw, i) => (
                          <tr
                            key={i}
                            onClick={() => toggleKeyword(kw)}
                            style={{ cursor: 'pointer', background: isSelected(kw) ? 'var(--blue-light)' : undefined }}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={isSelected(kw)}
                                onChange={() => toggleKeyword(kw)}
                                onClick={e => e.stopPropagation()}
                                style={{ width: 14, height: 14, cursor: 'pointer' }}
                              />
                            </td>
                            <td style={{ fontWeight: isSelected(kw) ? 500 : 400 }}>{kw.keyword}</td>
                            <td style={{ color: 'var(--gray-500)' }}>{kw.location}</td>
                            <td className="num">{(kw.search_volume || 0).toLocaleString()}</td>
                            <td className="num">{kw.rank || '100+'}</td>
                            <td>{getPriorityBadge(kw.priority)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
